import { LearningOppPageMetaData } from "@/components/LearningOppMetaData";
import {
  ColumnsSection,
  ColumnsSectionLeft,
  ColumnsSectionRight,
  ColumnsSectionSeperator,
} from "@/components/Sections/ColumnsSection";
import { SectionProps } from "@/components/Sections/Sections";
import { TeamMemberCard } from "@/components/TeamMemberCard";
import { MappedLearningOpportunity } from "@/server/backend-api";
import { LearningOpportunityStatus } from "@/server/backend-api/generated";
import { mcn } from "@/utils";
import { LearningOpportunityInfoMessage } from "../LearningOpportunityInfoMessage";
import { AttendingSectionStatic } from "@/_staticContent/learningOppSections";
import { IconClose } from "../Icons/IconClose";
import { OrganisationLinkButton } from "../OrganisationButton";

type MetaAndInfoColumnSectionProps = SectionProps & {
  heading: string;
  learningOpp: MappedLearningOpportunity;
  containerClassName?: string;
};

export function LearningOppMetaAndInfoColumnSection(
  props: MetaAndInfoColumnSectionProps
) {
  const {
    learningOpp,
    heading,
    className,
    containerClassName,
    ...sectionProps
  } = props;

  const attendingSectionStatic = AttendingSectionStatic;
  return (
    <>
      <ColumnsSection {...sectionProps} className={mcn("flex", className)}>
        <ColumnsSectionLeft className={mcn("space-y-xl", containerClassName)}>
          {learningOpp.maximumNumberOfSeats !== 0 &&
          learningOpp.status === LearningOpportunityStatus.OpenForApplication &&
          (learningOpp.simplifiedAdmissionProcessAndIsFull ||
            learningOpp.maximumNumberOfSeats ===
              learningOpp.numberOfSeatsOccupied) ? (
            <LearningOpportunityInfoMessage heading="All seats filled">
              There are no available seats for this learning opportunity.
            </LearningOpportunityInfoMessage>
          ) : null}
          {learningOpp.status === LearningOpportunityStatus.ComingSoon && (
            <LearningOpportunityInfoMessage heading="Coming soon">
              The enrollment period opens soon for this learning opportunity
            </LearningOpportunityInfoMessage>
          )}
          {learningOpp.maximumNumberOfSeats &&
          learningOpp.numberOfSeatsOccupied &&
          learningOpp.status === LearningOpportunityStatus.OpenForApplication &&
          !learningOpp.simplifiedAdmissionProcess
            ? (learningOpp.numberOfSeatsOccupied /
                learningOpp.maximumNumberOfSeats) *
                100 >=
                70 &&
              (learningOpp.numberOfSeatsOccupied /
                learningOpp.maximumNumberOfSeats) *
                100 <
                100 && (
                <LearningOpportunityInfoMessage heading="Only a few seats left!">
                  Interested in this learning opportunity? Apply right away to
                  increase your chances of securing a spot.
                </LearningOpportunityInfoMessage>
              )
            : null}
          {learningOpp.maximumNumberOfSeats &&
          learningOpp.numberOfSeatsOccupied &&
          learningOpp.status === LearningOpportunityStatus.OpenForApplication &&
          learningOpp.simplifiedAdmissionProcess
            ? (learningOpp.numberOfSeatsOccupied /
                learningOpp.maximumNumberOfSeats) *
                100 >=
                70 &&
              (learningOpp.numberOfSeatsOccupied /
                learningOpp.maximumNumberOfSeats) *
                100 <
                100 && (
                <LearningOpportunityInfoMessage
                  heading={
                    "Only " +
                    (learningOpp.maximumNumberOfSeats -
                      learningOpp.numberOfSeatsOccupied) +
                    " seats left!"
                  }
                >
                  If you want to join this learning opportunity you need to do
                  so now!
                </LearningOpportunityInfoMessage>
              )
            : null}
          <div>
            <h2 className="text-style-headline2 mb-xs text-light-yellow">
              {heading}
            </h2>
            <p className="text-style-body2">{learningOpp.introduction}</p>
          </div>
          <h3 className="text-style-headline4 text-light-yellow pt-12">
            The Team
          </h3>
          <h4 className="text-style-headline5">Teachers</h4>
          <p>{attendingSectionStatic.teacherInfoText[learningOpp.type]}</p>
          <div className="flex flex-wrap w-full">
            {learningOpp.teamchers.map((teamcher, index) => (
              <TeamMemberCard
                className="mr-8 mb-8 w-1/3"
                teamRole="Teacher"
                key={index}
                person={teamcher}
                teacherLabel={
                  AttendingSectionStatic.teacherTeamMemberLabel[
                    learningOpp.type
                  ]
                }
              />
            ))}
          </div>
          <h4 className="text-style-headline5">Learners</h4>
          <div className="bg-blue mr-8 flex flex-col items-center justify-center rounded-2 w-1/3 mb-8 h-32">
            <div>
              <IconClose className="h-12 w-12" />
            </div>
            <div>{learningOpp.numberOfSeatsOccupied} learners</div>
          </div>
          {learningOpp.challengeProviderOrganisations.length > 0 && (
            <>
              <h4 className="text-style-headline5">Challenge providers</h4>
              {learningOpp.challengeProviderOrganisations.map((provider) => (
                <div className="h-36 w-36" key={provider.id}>
                  <OrganisationLinkButton
                    key={provider.id}
                    type="organisation"
                    university={provider}
                  />
                </div>
              ))}
            </>
          )}
        </ColumnsSectionLeft>
        <ColumnsSectionSeperator />
        <ColumnsSectionRight className={containerClassName}>
          <LearningOppPageMetaData learningOpp={learningOpp} />
        </ColumnsSectionRight>
      </ColumnsSection>
    </>
  );
}
